import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { getResponseData } from '../models/api-response.model';
import {
  AuthUser,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
} from '../models/auth.model';
import { ApiService } from './api.service';
import { migrateStorefrontStorageKeys, STOREFRONT_AUTH_KEY } from '../constants/storefront-storage.constants';
import { isStorefrontToken } from '../utils/jwt-token.util';

migrateStorefrontStorageKeys();

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<AuthUser | null>(this.readStoredUser());
  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly authStatus$ = this.currentUser$.pipe(map((user) => Boolean(user?.token)));

  constructor(private api: ApiService) {}

  login(request: LoginRequest): Observable<AuthUser> {
    return this.api.post<AuthUser>('Account/login', request).pipe(
      map((response) => {
        const user = this.normalizeAuthUser(getResponseData(response, {} as AuthUser));
        if (!user) {
          throw new Error('Invalid login response');
        }
        return user;
      }),
      tap((user) => this.persistUser(user))
    );
  }

  register(request: RegisterRequest): Observable<AuthUser> {
    return this.api.post<AuthUser>('Account/userRegister', request).pipe(
      map((response) => {
        const user = this.normalizeAuthUser(getResponseData(response, {} as AuthUser));
        if (!user) {
          throw new Error('Invalid registration response');
        }
        return user;
      }),
      tap((user) => this.persistUser(user))
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<boolean> {
    return this.api.post<boolean>('Account/change-password', request).pipe(
      map((response) => getResponseData(response, false))
    );
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<boolean> {
    return this.api.post<unknown>('Account/forgot-password', request).pipe(
      map(() => true)
    );
  }

  resetPassword(request: ResetPasswordRequest): Observable<boolean> {
    return this.api.post<unknown>('Account/reset-password', request).pipe(
      map(() => true)
    );
  }

  getProfile(): Observable<AuthUser> {
    return this.api.get<AuthUser>('Account/me').pipe(
      map((response) => {
        const currentToken = this.getToken();
        if (!currentToken) {
          throw new Error('Not authenticated');
        }
        const user = this.normalizeAuthUser({
          ...getResponseData(response, {} as AuthUser),
          token: currentToken,
        });
        if (!user) {
          throw new Error('Invalid profile response');
        }
        return user;
      }),
      tap((user) => this.persistUser(user))
    );
  }

  updateProfile(request: UpdateProfileRequest): Observable<AuthUser> {
    return this.api.put<AuthUser>('Account/profile', request).pipe(
      map((response) => {
        const raw = getResponseData(response, {} as AuthUser);
        const token = raw.token || this.getToken() || '';
        const user = this.normalizeAuthUser({ ...raw, token });
        if (!user) {
          throw new Error('Invalid profile update response');
        }
        return user;
      }),
      tap((user) => this.persistUser(user))
    );
  }

  logout(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STOREFRONT_AUTH_KEY);
    }
    this.currentUserSubject.next(null);
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  getToken(): string | null {
    return this.currentUserSubject.value?.token ?? null;
  }

  getUserId(): string | null {
    return this.currentUserSubject.value?.userId ?? null;
  }

  isLoggedIn(): boolean {
    return Boolean(this.getToken());
  }

  private persistUser(user: AuthUser): void {
    if (!user?.token || !user?.userId) return;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STOREFRONT_AUTH_KEY, JSON.stringify(user));
    }
    this.currentUserSubject.next(user);
  }

  private readStoredUser(): AuthUser | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const raw = localStorage.getItem(STOREFRONT_AUTH_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AuthUser;
      const user = this.normalizeAuthUser(parsed);
      if (user && !isStorefrontToken(user.token)) {
        localStorage.removeItem(STOREFRONT_AUTH_KEY);
        return null;
      }
      return user;
    } catch {
      return null;
    }
  }

  /** Maps API user payload (camelCase or PascalCase) into AuthUser. */
  private normalizeAuthUser(raw: AuthUser | null | undefined): AuthUser | null {
    if (!raw) return null;
    const record = raw as AuthUser & {
      UserId?: string;
      Token?: string;
      FullName?: string;
      Email?: string;
      PhoneNumber?: string;
    };
    const userId = record.userId ?? record.UserId ?? '';
    const token = record.token ?? record.Token ?? '';
    if (!userId || !token) return null;
    return {
      userId,
      token,
      fullName: record.fullName ?? record.FullName ?? '',
      email: record.email ?? record.Email ?? '',
      phoneNumber: record.phoneNumber ?? record.PhoneNumber ?? '',
    };
  }
}
